// useMitgliederSuche.js
// -------------------------------------------------------------------------
// Personensuche für den Check-in (Empfang, Statistik-Werkzeug).
//
// Bis 03.10.2026 luden beide Seiten die Lead-Liste einmal beim Öffnen und
// filterten im Browser. Die Liste lieferte aber nur die 50 neuesten Leads
// (der Parameter für die Listengröße hieß falsch und wurde nie gelesen), nur eigene Leads
// für Rollen ohne Lead-Recht und keine archivierten. Jetzt fragt jede Eingabe
// den Server: GET /api/checkins/suche durchsucht alle Personen.
//
// Die Suche wartet kurz, bis nicht mehr getippt wird. Treffer werden nur
// gezeigt, solange sie zur aktuellen Eingabe gehören — eine Antwort auf eine
// ältere Eingabe erscheint nie unter dem Finger. Ein Fehler ist ein eigener
// Zustand: er darf nie wie „niemand gefunden" aussehen.
// -------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { checkinsAPI } from '@/lib/api';

export const SUCHE_MIN_ZEICHEN = 2;

const KEIN_ERGEBNIS = { fuer: null, anlauf: 0, treffer: [], mehr: false, fehler: false, keinRecht: false, zuKurz: false };

// Zweite Zeile eines Treffers: woran der Empfang zwei Personen unterscheidet.
// „Mitglied" steht nur bei laufendem Vertrag — fehlt es, ist das keine Aussage über die Person.
export function trefferZusatz(m) {
  return [
    m.mitglied ? 'Mitglied' : null,
    m.mitgliedsnummer ? `Nr. ${m.mitgliedsnummer}` : null,
    m.geburtsjahr ? `Jg. ${m.geburtsjahr}` : null,
    // Nur bei gleichnamigen Treffern geliefert: wann der Eintrag angelegt wurde (JJJJ-MM)
    m.angelegt ? `angelegt ${m.angelegt.slice(5, 7)}/${m.angelegt.slice(0, 4)}` : null,
    m.email || null,
    // E13 (15.10.2026): seit vier Wochen dabei, noch kein Ernährungs-Abo
    m.ernaehrung_anbieten ? 'Ernährungscoaching anbieten' : null,
  ].filter(Boolean).join(' · ');
}

export default function useMitgliederSuche(eingabe, { limit = 15, verzoegerungMs = 250 } = {}) {
  const q = (eingabe || '').trim();
  const gueltig = q.length >= SUCHE_MIN_ZEICHEN;
  const [ergebnis, setErgebnis] = useState(KEIN_ERGEBNIS);
  const [anlauf, setAnlauf] = useState(0);

  useEffect(() => {
    if (!gueltig) {
      setErgebnis(KEIN_ERGEBNIS);
      return undefined;
    }
    let aktuell = true;
    const timer = setTimeout(async () => {
      try {
        const res = await checkinsAPI.suche(q, limit);
        if (!aktuell) return;
        // zu_kurz: der Server zählt nur Buchstaben und Ziffern — „O'" ist für ihn ein Zeichen
        setErgebnis({ fuer: q, anlauf, treffer: res.data?.treffer || [], mehr: !!res.data?.mehr, fehler: false, keinRecht: false, zuKurz: !!res.data?.zu_kurz });
      } catch (err) {
        if (!aktuell) return;
        // 403 ist kein Ausfall: erneut suchen hilft nicht, das Konto darf nicht suchen
        setErgebnis({ fuer: q, anlauf, treffer: [], mehr: false, fehler: true, keinRecht: err?.response?.status === 403, zuKurz: false });
      }
    }, verzoegerungMs);
    return () => { aktuell = false; clearTimeout(timer); };
  }, [q, gueltig, limit, verzoegerungMs, anlauf]);

  const erneut = useCallback(() => setAnlauf((n) => n + 1), []);

  // Nur ein Ergebnis für genau diese Eingabe (und diesen Anlauf) zählt.
  const passt = gueltig && ergebnis.fuer === q && ergebnis.anlauf === anlauf;
  return {
    treffer: passt ? ergebnis.treffer : [],
    mehr: passt && ergebnis.mehr,
    fehler: passt && ergebnis.fehler,
    keinRecht: passt && ergebnis.keinRecht,
    laedt: gueltig && !passt,
    leer: q.length === 0,
    zuKurz: (q.length > 0 && !gueltig) || (passt && ergebnis.zuKurz),
    erneut,
  };
}
